// React
import {
  forwardRef,
  type ElementRef,
  type ComponentPropsWithoutRef,
  type Ref,
} from "react"

// Libraries
import * as SelectPrimitive from "@radix-ui/react-select"

// UI primitives
import { FIELD_TRIGGER_CLASSES } from "@/components/ui/field-trigger"
import { FIELD_ARIA_INVALID_PAINT } from "@/components/ui/field-surface"

// Icons
import { ChevronDownIcon } from "@/react/components/icons/ChevronDownIcon"

// Styles
import { selectStyles, getSelectContentClasses, getSelectViewportClasses } from "./select.styles"

// Hooks and utilities
import { cn } from "@/lib/utils"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn("group", FIELD_TRIGGER_CLASSES, FIELD_ARIA_INVALID_PAINT, className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon className="shrink-0 text-off-black chevron-motion group-data-[state=open]:rotate-180" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    viewportClassName?: string
  }
>(({ className, children, position = "popper", viewportClassName, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(getSelectContentClasses(position), className)}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(getSelectViewportClasses(position), viewportClassName)}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = forwardRef<
  ElementRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(selectStyles.label, className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

function SelectItem({
  ref,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  ref?: Ref<ElementRef<typeof SelectPrimitive.Item>>
}) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(selectStyles.item, className)}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = forwardRef<
  ElementRef<typeof SelectPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(selectStyles.separator, className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}
